import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  Share2,
  Heart,
  Star,
  MapPin,
  Clock,
  PawPrint,
  Image,
  ListChecks,
  Sparkles,
  ChevronDown,
  Home
} from 'lucide-react';
import { staysApi } from '../services/staysApi';
import { checkStayHasAvailability } from '../services/inventoryApi';
import { useDebouncedEffect } from '../hooks/useDebouncedEffect';
import PawStories from '../components/PawStories';
import MeetTheFounders from '../components/MeetTheFounders';
import { getStayAmenityDisplayLines } from '../utils/listingAttributesDisplay';
import FoundersVerifiedBadge from '../components/FoundersVerifiedBadge';
import { getVisibleDotIndices } from '../utils/carouselDots';
import FoundersReviewCarousel from '../components/FoundersReviewCarousel';
import { shareListing } from '../utils/wishlist';
import {
  dateToYmd,
  minDiscountedAcrossWeek
} from '../utils/roomDayPricing';
import {
  bookableRooms,
  isComingSoonDiscountedPrice,
  isStayComingSoon,
  minStayBookablePriceAcrossRooms
} from '../utils/stayComingSoon';
import { wishlistAPI } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { useDateContext } from '../contexts/DateContext';
import { useGuestContext } from '../contexts/GuestContext';
import ResponsiveImage from '../components/ResponsiveImage';
import PhotoGalleryModal from '../components/PhotoGalleryModal';
import { FilterDashedSeparator } from '../components/FilterDashedSeparator';
import { collectStayGalleryMedia, galleryMediaToUrls, stayGalleryCoverImageUrl } from '../utils/galleryMedia';
import GallerySlideMedia from '../components/GallerySlideMedia';

/** Category rows with a numeric score (shown on property page). */
function ratingsEntriesForDisplay(stay) {
  const r = stay?.ratings;
  if (!r || typeof r !== 'object') return [];
  return Object.entries(r).filter(([, data]) => {
    if (!data || typeof data !== 'object') return false;
    return Number.isFinite(Number(data.score));
  });
}

function pawLovedDisplayPercent(stay) {
  const n = Number(stay?.pawLoved);
  if (!Number.isFinite(n) || n < 1 || n > 100) return null;
  return Math.min(100, Math.max(1, Math.round(n)));
}

/** Spaced rule names from CMS stay as-is; legacy camelCase gets one-time spacing. */
function houseRuleDisplayLabel(ruleName) {
  const s = String(ruleName || '').trim();
  if (!s) return s;
  if (/\s/.test(s)) return s;
  return s.replace(/([A-Z])/g, ' $1').trim();
}

const DEFAULT_PROPERTY_GALLERY_IMAGES = [
  'https://images.unsplash.com/photo-1587300003388-59208cc962cb?w=800',
  'https://images.unsplash.com/photo-1583511655857-d19b40a7a54e?w=800'
];

const PropertyDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [currentImage, setCurrentImage] = useState(0);
  const [showGallery, setShowGallery] = useState(false);
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [saved, setSaved] = useState(false);
  const [mobileAmenitiesOpen, setMobileAmenitiesOpen] = useState(false);
  const [mobileAboutOpen, setMobileAboutOpen] = useState(false);
  const [mobileAroundOpen, setMobileAroundOpen] = useState(false);
  const [mobileHouseRulesOpen, setMobileHouseRulesOpen] = useState(false);
  /** idle | loading | available | sold_out | no_dates */
  const [footerAvailability, setFooterAvailability] = useState('idle');
  const { isAuthenticated } = useAuth();
  const { selectedDates: globalDates } = useDateContext();
  const { guestData } = useGuestContext();

  // Fetch property data when component mounts
  useEffect(() => {
    const fetchProperty = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const data = await staysApi.getStayById(id);
        console.log('API Response:', data);
        
        // Extract the stay object from the response
        const stayData = data.stay || data;
        console.log('Stay Data:', stayData);
        
        setProperty(stayData);
      } catch (err) {
        console.error('Error fetching property:', err);
        setError(err.message || 'Failed to load property details');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchProperty();
    }
  }, [id]);

  useEffect(() => {
    const checkSavedStatus = async () => {
      if (!id || !isAuthenticated) {
        setSaved(false);
        return;
      }

      try {
        const response = await wishlistAPI.getWishlist();
        const isSaved = (response.items || []).some((item) => item.type === 'property' && item.id === String(id));
        setSaved(isSaved);
      } catch (error) {
        console.error('Failed to fetch wishlist:', error);
        setSaved(false);
      }
    };

    checkSavedStatus();
  }, [id, isAuthenticated]);

  const amenityLines = useMemo(
    () => getStayAmenityDisplayLines(property),
    [property]
  );

  const galleryMedia = useMemo(
    () => collectStayGalleryMedia(property),
    [property]
  );

  const galleryUrls = useMemo(() => galleryMediaToUrls(galleryMedia), [galleryMedia]);

  const galleryPoster = useMemo(
    () => stayGalleryCoverImageUrl(property) || undefined,
    [property]
  );

  const stayComingSoon = useMemo(
    () => isStayComingSoon(property),
    [property]
  );

  /** Lowest nightly rate: min across bookable rooms only. */
  const footerStartingPrice = useMemo(() => {
    const rooms = bookableRooms(property?.rooms);
    if (!rooms.length) return null;

    const paramIn = searchParams.get('checkIn');
    const paramOut = searchParams.get('checkOut');
    const checkInYmd =
      (paramIn && paramIn.slice(0, 10)) ||
      (globalDates?.checkInDate ? dateToYmd(globalDates.checkInDate) : null);
    const checkOutYmd =
      (paramOut && paramOut.slice(0, 10)) ||
      (globalDates?.checkOutDate ? dateToYmd(globalDates.checkOutDate) : null);
    const adults = Math.max(1, Math.floor(Number(guestData?.adults) || 2));

    if (checkInYmd && checkOutYmd && checkInYmd < checkOutYmd) {
      return minStayBookablePriceAcrossRooms(rooms, checkInYmd, checkOutYmd, adults);
    }

    let minDp = Infinity;
    let minMrp = 0;
    for (const room of rooms) {
      const dp = minDiscountedAcrossWeek(room);
      if (dp == null || !Number.isFinite(dp)) continue;
      if (isComingSoonDiscountedPrice(dp)) continue;
      if (dp < minDp) {
        minDp = dp;
        const p = room.price || {};
        minMrp = Number(p.mrp) || dp;
      }
    }
    if (minDp === Infinity) return null;
    return { discountedPrice: minDp, mrp: minMrp };
  }, [property, searchParams, globalDates, guestData]);

  const stayCheckInYmd = useMemo(() => {
    const paramIn = searchParams.get('checkIn');
    return (
      (paramIn && paramIn.slice(0, 10)) ||
      (globalDates?.checkInDate ? dateToYmd(globalDates.checkInDate) : null)
    );
  }, [searchParams, globalDates]);

  const stayCheckOutYmd = useMemo(() => {
    const paramOut = searchParams.get('checkOut');
    return (
      (paramOut && paramOut.slice(0, 10)) ||
      (globalDates?.checkOutDate ? dateToYmd(globalDates.checkOutDate) : null)
    );
  }, [searchParams, globalDates]);

  const guestRoomCount = Math.max(1, Math.floor(Number(guestData?.rooms) || 1));

  useDebouncedEffect(
    () => {
      if (!property?.rooms?.length || !id) {
        setFooterAvailability('idle');
        return undefined;
      }
      if (!stayCheckInYmd || !stayCheckOutYmd || stayCheckInYmd >= stayCheckOutYmd) {
        setFooterAvailability('no_dates');
        return undefined;
      }

      let cancelled = false;
      setFooterAvailability('loading');

      checkStayHasAvailability({
        stayId: id,
        rooms: property.rooms,
        checkIn: stayCheckInYmd,
        checkOut: stayCheckOutYmd,
        quantity: guestRoomCount
      })
        .then((result) => {
          if (!cancelled) {
            setFooterAvailability(result.canAccommodate ? 'available' : 'sold_out');
          }
        })
        .catch(() => {
          if (!cancelled) setFooterAvailability('available');
        });

      return () => {
        cancelled = true;
      };
    },
    [property, id, stayCheckInYmd, stayCheckOutYmd, guestRoomCount],
    450
  );

  const footerSoldOut = footerAvailability === 'sold_out';
  const footerComingSoon = stayComingSoon;
  const footerChecking = footerAvailability === 'loading';
  const footerHasDates =
    footerAvailability === 'available' || footerAvailability === 'sold_out';

  useEffect(() => {
    const len =
      galleryUrls.length > 0
        ? galleryUrls.length
        : DEFAULT_PROPERTY_GALLERY_IMAGES.length;
    setCurrentImage((idx) => Math.min(Math.max(0, idx), Math.max(0, len - 1)));
  }, [galleryUrls.length]);

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAF7F2] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black mx-auto mb-4"></div>
          <p className="text-gray-600">Loading property details...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-[#FAF7F2] flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 text-6xl mb-4">❌</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Error Loading Property</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button 
            onClick={() => navigate('/stays')}
            className="bg-black text-white px-6 py-3 rounded-lg hover:bg-gray-800 transition-colors"
          >
            Back to Stays
          </button>
        </div>
      </div>
    );
  }

  // No property found
  if (!property) {
    return (
      <div className="min-h-screen bg-[#FAF7F2] flex items-center justify-center">
        <div className="text-center">
          <div className="text-gray-500 text-6xl mb-4">🏠</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Property Not Found</h2>
          <p className="text-gray-600 mb-4">The property you're looking for doesn't exist.</p>
          <button 
            onClick={() => navigate('/stays')}
            className="bg-black text-white px-6 py-3 rounded-lg hover:bg-gray-800 transition-colors"
          >
            Back to Stays
          </button>
        </div>
      </div>
    );
  }

  // Touch/swipe functions for mobile carousel
  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe && currentImage < images.length - 1) {
      // Swipe left - go to next image
      handleImageChange(currentImage + 1);
    } else if (isRightSwipe && currentImage > 0) {
      // Swipe right - go to previous image
      handleImageChange(currentImage - 1);
    }
  };

  const handleImageChange = (newIndex) => {
    setCurrentImage(newIndex);
  };

  // Function to get category-specific icons
  const getCategoryIcon = (category) => {
    const categoryLower = category.toLowerCase();
    
    switch (categoryLower) {
      case 'staff':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="17" viewBox="0 0 16 17" fill="none">
            <g clip-path="url(#clip0_2846_7535)">
              <path d="M6 7.53687C6 8.0673 6.21071 8.57601 6.58579 8.95108C6.96086 9.32615 7.46957 9.53687 8 9.53687C8.53043 9.53687 9.03914 9.32615 9.41421 8.95108C9.78929 8.57601 10 8.0673 10 7.53687C10 7.00643 9.78929 6.49772 9.41421 6.12265C9.03914 5.74758 8.53043 5.53687 8 5.53687C7.46957 5.53687 6.96086 5.74758 6.58579 6.12265C6.21071 6.49772 6 7.00643 6 7.53687Z" stroke="black" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M4 14.8702V14.2035C4 13.4963 4.28095 12.818 4.78105 12.3179C5.28115 11.8178 5.95942 11.5369 6.66667 11.5369H9.33333C10.0406 11.5369 10.7189 11.8178 11.219 12.3179C11.719 12.818 12 13.4963 12 14.2035V14.8702" stroke="black" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M2 4.20345C2 3.84983 2.14048 3.51069 2.39052 3.26064C2.64057 3.01059 2.97971 2.87012 3.33333 2.87012H12.6667C13.0203 2.87012 13.3594 3.01059 13.6095 3.26064C13.8595 3.51069 14 3.84983 14 4.20345V13.5368C14 13.8904 13.8595 14.2295 13.6095 14.4796C13.3594 14.7296 13.0203 14.8701 12.6667 14.8701H3.33333C2.97971 14.8701 2.64057 14.7296 2.39052 14.4796C2.14048 14.2295 2 13.8904 2 13.5368V4.20345Z" stroke="black" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round"/>
            </g>
            <defs>
              <clipPath id="clip0_2846_7535">
                <rect width="16" height="16" fill="white" transform="translate(0 0.870117)"/>
              </clipPath>
            </defs>
          </svg>
        );
      case 'location':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="17" viewBox="0 0 16 17" fill="none">
            <g clip-path="url(#clip0_2846_7548)">
              <path d="M2 5.53687L6 3.53687L10 5.53687L14 3.53687V12.2035L10 14.2035L6 12.2035L2 14.2035V5.53687Z" stroke="black" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M6 8.87012V8.87678" stroke="black" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M4 9.53687V9.54353" stroke="black" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M11.3337 10.87L8.66699 8.20337" stroke="black" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M8.66699 10.87L11.3337 8.20337" stroke="black" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round"/>
            </g>
            <defs>
              <clipPath id="clip0_2846_7548">
                <rect width="16" height="16" fill="white" transform="translate(0 0.870117)"/>
              </clipPath>
            </defs>
          </svg>
        );
      case 'roomcomfort':
      case 'room comfort':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="17" viewBox="0 0 16 17" fill="none">
            <g clip-path="url(#clip0_2846_7563)">
              <path d="M3.33301 6.8702C3.33301 7.22382 3.47348 7.56296 3.72353 7.81301C3.97358 8.06306 4.31272 8.20353 4.66634 8.20353C5.01996 8.20353 5.3591 8.06306 5.60915 7.81301C5.8592 7.56296 5.99967 7.22382 5.99967 6.8702C5.99967 6.51658 5.8592 6.17744 5.60915 5.92739C5.3591 5.67734 5.01996 5.53687 4.66634 5.53687C4.31272 5.53687 3.97358 5.67734 3.72353 5.92739C3.47348 6.17744 3.33301 6.51658 3.33301 6.8702Z" stroke="black" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M14.6663 12.2034V10.2034H1.33301" stroke="black" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M1.33301 6.20337V12.2034" stroke="black" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M8 10.2035H14.6667V8.87012C14.6667 8.33968 14.456 7.83098 14.0809 7.4559C13.7058 7.08083 13.1971 6.87012 12.6667 6.87012H8V10.2035Z" stroke="black" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round"/>
            </g>
            <defs>
              <clipPath id="clip0_2846_7563">
                <rect width="16" height="16" fill="white" transform="translate(0 0.870117)"/>
              </clipPath>
            </defs>
          </svg>
        );
      case 'cleanliness':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="17" viewBox="0 0 16 17" fill="none">
            <g clip-path="url(#clip0_2846_7577)">
              <path d="M2 4.87012C2 4.33968 2.21071 3.83098 2.58579 3.4559C2.96086 3.08083 3.46957 2.87012 4 2.87012H12C12.5304 2.87012 13.0391 3.08083 13.4142 3.4559C13.7893 3.83098 14 4.33968 14 4.87012V12.8701C14 13.4006 13.7893 13.9093 13.4142 14.2843C13.0391 14.6594 12.5304 14.8701 12 14.8701H4C3.46957 14.8701 2.96086 14.6594 2.58579 14.2843C2.21071 13.9093 2 13.4006 2 12.8701V4.87012Z" stroke="black" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M2 8.20345L7.33333 2.87012" stroke="black" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M2 12.2034L11.3333 2.87012" stroke="black" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round"/>
            </g>
            <defs>
              <clipPath id="clip0_2846_7577">
                <rect width="16" height="16" fill="white" transform="translate(0 0.870117)"/>
              </clipPath>
            </defs>
          </svg>
        );

      case 'wifi':
        return (
          <svg className="w-5 h-5 text-blue-500" fill="currentColor" viewBox="0 0 24 24">
            <path d="M1 9l2 2c4.97-4.97 13.03-4.97 18 0l2-2C16.93 2.93 7.08 2.93 1 9zm8 8l3 3 3-3c-1.65-1.66-4.34-1.66-6 0zm-4-4l2 2c2.76-2.76 7.24-2.76 10 0l2-2C15.14 9.14 8.87 9.14 5 13z"/>
          </svg>
        );
      case 'parking':
        return (
          <svg className="w-5 h-5 text-gray-700" fill="currentColor" viewBox="0 0 24 24">
            <path d="M13 3H6v18h4v-6h3c3.31 0 6-2.69 6-6s-2.69-6-6-6zm0 10h-3V9h3c1.66 0 3 1.34 3 3s-1.34 3-3 3z"/>
          </svg>
        );

    }
  };

  const images =
    galleryUrls.length > 0 ? galleryUrls : DEFAULT_PROPERTY_GALLERY_IMAGES;

  const title = property.title || property.name || 'Property';
  const city = property.city || 'Location';
  const state = property.state || '';
  const address = property.address || 'Address not available';
  const rating = property.rating || 0;
  const ratingTag = property.ratingTag || 'No rating';
  const founderVerified = property.founderVerified || false;
  const aboutPropertyText =
    property.description != null ? String(property.description).trim() : '';
  const features = property.features || [];
  const nearestLandmarks = (property.nearestLandmarks || []).filter((place) => {
    if (!place || typeof place !== 'object') return false;
    const label = String(place.location || place.name || '').trim();
    return label.length > 0;
  });

  // Handle house rules structure - convert array to object for display
  const houseRulesArray = property.houseRules || [];
  const houseRules = houseRulesArray.reduce((acc, rule) => {
    if (rule.ruleName && rule.ruleValue) {
      const key = houseRuleDisplayLabel(rule.ruleName);
      acc[key] = rule.ruleValue;
    }
    return acc;
  }, {});

  const ratingRows = ratingsEntriesForDisplay(property);
  const showPawRatingsCard = ratingRows.length > 0;
  const pawPct = pawLovedDisplayPercent(property);
  const showRatingsOrHouseRulesSection =
    showPawRatingsCard || Object.keys(houseRules).length > 0;

  const handleSaveProperty = async () => {
    if (!isAuthenticated) {
      alert('Please login to save favourites to your account.');
      navigate('/account');
      return;
    }

    try {
      if (saved) {
        await wishlistAPI.removeFromWishlist('property', id);
        setSaved(false);
        return;
      }

      await wishlistAPI.addToWishlist({
        id,
        type: 'property',
        title,
        subtitle: [city, state].filter(Boolean).join(', '),
        image: images[0],
        path: `/property/${id}`
      });
      setSaved(true);
    } catch (error) {
      console.error('Failed to update wishlist:', error);
      alert('Could not update wishlist. Please try again.');
    }
  };

  const handleShareProperty = async () => {
    const shared = await shareListing({ title, path: `/property/${id}` });
    if (shared) {
      alert('Link shared successfully.');
    } else {
      alert('Could not share right now. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF7F2] md:py-[60px] px-0">
      {/* Hero Section with Gallery */}
      <div className="relative">
        {/* Mobile: Full width image with carousel */}

        <div className="md:hidden relative aspect-square overflow-hidden">
          <div
            className="relative h-full w-full"
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
          >
            <GallerySlideMedia
              key={currentImage}
              src={images[currentImage]}
              poster={galleryPoster}
              variant="hero"
              priority
              active
              alt={`${title} ${currentImage + 1}`}
              className="h-full w-full object-cover"
            />
          </div>
          <div className='h-[46px] absolute bottom-0 left-0 w-full '
          style={{
            background: 'linear-gradient(178deg,rgb(121, 121, 121, 0) 0%,rgb(19, 19, 19, 0.33) 100%)',
            backdropFilter: 'blur(8px)',
            mask: 'linear-gradient(180deg, rgba(0, 0, 0, 0.00) 0%, rgba(255, 255, 255) 100%)',
          }}
          ></div>
          <div className='h-[46px] absolute top-0 left-0 w-full '
          style={{
            background: 'linear-gradient(360deg,rgb(121, 121, 121, 0) 0%,rgb(19, 19, 19, 0.33) 100%)',
            backdropFilter: 'blur(8px)',
            mask: 'linear-gradient(0deg, rgba(0, 0, 0, 0.00) 0%, rgba(255, 255, 255) 100%)',
          }}
          ></div>
          {/* Back Button */}
          <Link to="/stays" className="absolute top-4 left-4 z-10">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
            <g clip-path="url(#clip0_2846_7407)">
              <path d="M5 12.0001H19" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M5 12.0001L11 18.0001" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M5 12.0001L11 6.00012" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </g>
            <defs>
              <clipPath id="clip0_2846_7407">
                <rect width="24" height="24" fill="white"/>
              </clipPath>
            </defs>
          </svg>
          </Link>
        <div className="absolute bottom-4 right-4 flex flex-col items-end gap-[16px]">
          {/* Gallery Icon - Mobile */}
          <button 
            onClick={() => setShowGallery(true)}
            className=" bg-black bg-opacity-60 rounded-full p-[6px] z-10 hover:bg-opacity-80 transition-colors"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none">
              <g clipPath="url(#clip0_250_25568)">
                <path d="M19 3H15C14.4696 3 13.9609 3.21071 13.5858 3.58579C13.2107 3.96086 13 4.46957 13 5V17C13 18.0609 13.4214 19.0783 14.1716 19.8284C14.9217 20.5786 15.9391 21 17 21C18.0609 21 19.0783 20.5786 19.8284 19.8284C20.5786 19.0783 21 18.0609 21 17V5C21 4.46957 20.7893 3.96086 20.4142 3.58579C20.0391 3.21071 19.5304 3 19 3Z" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M13.0004 7.34998L11.0004 5.34998C10.6253 4.97503 10.1167 4.7644 9.58636 4.7644C9.05603 4.7644 8.54742 4.97503 8.17236 5.34998L5.34436 8.17798C4.96942 8.55303 4.75879 9.06165 4.75879 9.59198C4.75879 10.1223 4.96942 10.6309 5.34436 11.006L14.3444 20.006" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M7.3 13H5C4.46957 13 3.96086 13.2107 3.58579 13.5858C3.21071 13.9609 3 14.4696 3 15V19C3 19.5304 3.21071 20.0391 3.58579 20.4142C3.96086 20.7893 4.46957 21 5 21H17" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </g>
              <defs>
                <clipPath id="clip0_250_25568">
                  <rect width="24" height="24" fill="white"/>
                </clipPath>
              </defs>
            </svg>
          </button>

          {/* Image Indicators */}
          <div className="flex space-x-[6px] items-center z-10">
            {getVisibleDotIndices(images.length, currentImage).map((index) => (
              <button
                key={index}
                type="button"
                aria-label={`Image ${index + 1}`}
                onClick={() => handleImageChange(index)}
                className={`bg-white rounded-full ${
                  index === currentImage ? 'w-[14px] h-[14px]' : 'w-[6.5px] h-[6.5px]'
                }`}
              />
            ))}
          </div>
        </div>
         
        </div>

        {/* Desktop: Grid layout with gallery icon */}
        <div className="hidden md:block px-0 md:px-[60px] lg:px-[120px]">
          <div className="flex flex-col">
            {/* Left Column - Image Gallery */}
            <div className="relative">
              <div className="grid grid-cols-3 gap-[20px]">
                <div className="col-span-2">
                  <div className="h-[50vh] overflow-hidden">
                    <GallerySlideMedia
                      src={images[currentImage]}
                      poster={galleryPoster}
                      variant="cover"
                      priority
                      active
                      alt={title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
                <div className="h-[50vh] flex flex-col gap-[20px]">
                  {images.slice(1, 3).map((image, index) => (
                    <div key={index} className="relative h-[calc(50%-10px)] overflow-hidden">
                      <GallerySlideMedia
                        src={image}
                        poster={galleryPoster}
                        variant="side"
                        alt={`${title} ${index + 2}`}
                        active={false}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ))}
                </div>
              </div>
              {/* Gallery Icon */}
              <button 
                onClick={() => setShowGallery(true)}
                className="absolute bottom-[16px] right-[16px] rounded-full p-[8px] bg-[#131313] bg-opacity-60 cursor-pointer transition-colors"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <g clipPath="url(#clip0_250_25568)">
                    <path d="M19 3H15C14.4696 3 13.9609 3.21071 13.5858 3.58579C13.2107 3.96086 13 4.46957 13 5V17C13 18.0609 13.4214 19.0783 14.1716 19.8284C14.9217 20.5786 15.9391 21 17 21C18.0609 21 19.0783 20.5786 19.8284 19.8284C20.5786 19.0783 21 18.0609 21 17V5C21 4.46957 20.7893 3.96086 20.4142 3.58579C20.0391 3.21071 19.5304 3 19 3Z" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M13.0004 7.34998L11.0004 5.34998C10.6253 4.97503 10.1167 4.7644 9.58636 4.7644C9.05603 4.7644 8.54742 4.97503 8.17236 5.34998L5.34436 8.17798C4.96942 8.55303 4.75879 9.06165 4.75879 9.59198C4.75879 10.1223 4.96942 10.6309 5.34436 11.006L14.3444 20.006" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M7.3 13H5C4.46957 13 3.96086 13.2107 3.58579 13.5858C3.21071 13.9609 3 14.4696 3 15V19C3 19.5304 3.21071 20.0391 3.58579 20.4142C3.96086 20.7893 4.46957 21 5 21H17" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </g>
                  <defs>
                    <clipPath id="clip0_250_25568">
                      <rect width="24" height="24" fill="white"/>
                    </clipPath>
                  </defs>
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Property Info */}
      <div className="flex flex-col md:flex-row items-start bg-[#FAF7F2] justify-between gap-[20px] px-[16px] py-[20px] md:px-[60px] lg:px-[120px]">
        <div>
          <div className="flex flex-col items-start justify-between">
            <h1 className="text-[20px] font-normal tracking-[-0.5px] leading-none text-black flex items-center">
              {title}
            </h1>
            <p className="text-[20px] font-normal tracking-[-0.5px] leading-none text- mb-[20px]">{city}{state ? `, ${state}` : ''}</p>
            <p className="text-[14px] font-normal tracking-[-0.5px] leading-none text-[#bebebe]">{address}</p>
          </div>
          
          <div className='w-full h-0 border-b border-[#f0f0f0] border-dashed my-[20px]'></div>

          {/* Rating and Badges */}
          <div className="flex items-center space-x-3">
            <div className="flex items-center gap-[10px]">
              <span className="text-[14px] text-[#fff] bg-black rounded-[14px] w-[34px] h-[34px] flex items-center justify-center">{rating}</span> {ratingTag}
            </div>
            {founderVerified && <FoundersVerifiedBadge />}
          </div>
        </div>
        
        <div className='w-full block md:hidden h-0 border-b border-[#f0f0f0] border-dashed'></div>
        
        {/* Actions */}
        <div className="flex items-center space-x-[20px] mb-4">
          <button onClick={handleSaveProperty} className="flex items-center text-[#FF5E4C] gap-[4px] font-medium">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="25" viewBox="0 0 24 25" fill="none">
              <g clipPath="url(#clip0_174_6517)">
                <path d="M19.5003 13.442L12.0003 20.87L4.5003 13.442C4.00561 12.9607 3.61594 12.3821 3.35585 11.7427C3.09575 11.1033 2.97086 10.417 2.98904 9.72698C3.00721 9.03697 3.16806 8.35819 3.46146 7.73339C3.75485 7.10859 4.17444 6.5513 4.69379 6.09663C5.21314 5.64196 5.82101 5.29974 6.47911 5.09153C7.13722 4.88332 7.83131 4.81363 8.51767 4.88685C9.20403 4.96007 9.8678 5.17461 10.4672 5.51696C11.0666 5.85931 11.5885 6.32205 12.0003 6.87605C12.4138 6.32607 12.9364 5.86737 13.5354 5.52865C14.1344 5.18994 14.7968 4.9785 15.4812 4.90757C16.1657 4.83664 16.8574 4.90775 17.5131 5.11644C18.1688 5.32514 18.7743 5.66692 19.2919 6.12041C19.8094 6.5739 20.2277 7.12933 20.5207 7.75194C20.8137 8.37455 20.975 9.05093 20.9946 9.73876C21.0142 10.4266 20.8916 11.111 20.6344 11.7493C20.3773 12.3876 19.9912 12.9659 19.5003 13.448" stroke="#FF5E4C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </g>
              <defs>
                <clipPath id="clip0_174_6517">
                  <rect width="24" height="24" fill="white" transform="translate(0 0.870117)"/>
                </clipPath>
              </defs>
            </svg>
            {saved ? 'Saved' : 'Save'}
          </button>
          <button onClick={handleShareProperty} className="flex items-center text-[#FF5E4C] gap-[4px] font-medium">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="25" viewBox="0 0 24 25" fill="none">
              <g clipPath="url(#clip0_174_6523)">
                <path d="M3 12.8701C3 13.6658 3.31607 14.4288 3.87868 14.9914C4.44129 15.554 5.20435 15.8701 6 15.8701C6.79565 15.8701 7.55871 15.554 8.12132 14.9914C8.68393 14.4288 9 13.6658 9 12.8701C9 12.0745 8.68393 11.3114 8.12132 10.7488C7.55871 10.1862 6.79565 9.87012 6 9.87012C5.20435 9.87012 4.44129 10.1862 3.87868 10.7488C3.31607 11.3114 3 12.0745 3 12.8701Z" stroke="#FF5E4C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M15 6.87012C15 7.66577 15.3161 8.42883 15.8787 8.99144C16.4413 9.55405 17.2044 9.87012 18 9.87012C18.7956 9.87012 19.5587 9.55405 20.1213 8.99144C20.6839 8.42883 21 7.66577 21 6.87012C21 6.07447 20.6839 5.31141 20.1213 4.7488C19.5587 4.18619 18.7956 3.87012 18 3.87012C17.2044 3.87012 16.4413 4.18619 15.8787 4.7488C15.3161 5.31141 15 6.07447 15 6.87012Z" stroke="#FF5E4C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M15 18.8701C15 19.6658 15.3161 20.4288 15.8787 20.9914C16.4413 21.554 17.2044 21.8701 18 21.8701C18.7956 21.8701 19.5587 21.554 20.1213 20.9914C20.6839 20.4288 21 19.6658 21 18.8701C21 18.0745 20.6839 17.3114 20.1213 16.7488C19.5587 16.1862 18.7956 15.8701 18 15.8701C17.2044 15.8701 16.4413 16.1862 15.8787 16.7488C15.3161 17.3114 15 18.0745 15 18.8701Z" stroke="#FF5E4C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M8.7002 11.5702L15.3002 8.17017" stroke="#FF5E4C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M8.7002 14.1702L15.3002 17.5702" stroke="#FF5E4C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </g>
              <defs>
                <clipPath id="clip0_174_6523">
                  <rect width="24" height="24" fill="white" transform="translate(0 0.870117)"/>
                </clipPath>
              </defs>
            </svg>
            Share
          </button>
        </div>
      </div>

      {/* Desktop Pricing */}
      <div className="fixed bottom-0 md:bottom-[40px] z-50 left-0 right-0 md:left-1/2 md:-translate-x-1/2 bg-white border-t md:border border-[#dddddd] px-[16px] py-[20px] md:p-[12px] md:rounded-[24px] w-full max-w-[800px] ">
        <div className="flex items-center justify-between">
          <div className='flex md:flex-row flex-col md:items-center gap-[10px]'>
            {footerComingSoon ? (
              <p className="text-[20px] font-bold text-[#78736A] tracking-[-0.02em]">
                Coming soon
              </p>
            ) : footerSoldOut ? (
              <p className="text-[20px] font-bold text-[#78736A] tracking-[-0.02em]">
                Sold out
              </p>
            ) : (
              <>
                <p className="text-[14px] text-[#000]">
                  {footerHasDates ? 'From' : 'Starting from'}
                </p>
                <div className="flex items-center space-x-2">
                  <span className="text-[20px] font-bold">
                    {footerStartingPrice
                      ? `₹${Math.round(footerStartingPrice.discountedPrice).toLocaleString('en-IN')}`
                      : 'Contact for pricing'}
                  </span>
                </div>
              </>
            )}
          </div>
          <button 
            type="button"
            data-book-sniff="true"
            disabled={footerComingSoon || footerSoldOut || footerChecking}
            onClick={() => {
              if (footerComingSoon || footerSoldOut || footerChecking) return;
              const queryString = searchParams.toString();
              const roomsUrl = queryString 
                ? `/property/${id}/rooms?${queryString}`
                : `/property/${id}/rooms`;
              
              navigate(roomsUrl, { state: { property } });
            }}
            className={`text-[16px] h-[56px] leading-[1.1] px-[40px] md:px-[60px] py-[14px] rounded-[14px] font-medium transition-colors ${
              footerComingSoon || footerSoldOut || footerChecking
                ? 'bg-[#E5E3DF] text-[#78736A] cursor-not-allowed'
                : 'bg-black text-white hover:bg-gray-800'
            }`}
          >
            {footerChecking
              ? 'Checking…'
              : footerComingSoon
                ? 'Coming soon'
                : footerSoldOut
                  ? 'Sold out'
                  : 'Select Room'}
          </button>
        </div>
      </div>

      <PhotoGalleryModal
        open={showGallery}
        onClose={() => setShowGallery(false)}
        images={images}
        defaultPoster={galleryPoster}
        venueTitle={[title, [city, state].filter(Boolean).join(', ')].filter(Boolean).join(' — ')}
        initialIndex={currentImage}
      />

      {/* Highlights Section */}
      <div className="px-[16px] md:px-[60px] lg:px-[120px]">
        <div className="w-full p-[20px] bg-white rounded-[16px]">
          <h2 className="text-[18px] font-medium text-black mb-[20px] flex gap-[3px] items-center">
            Highlights
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="25" viewBox="0 0 24 25" fill="none">
              <g clip-path="url(#clip0_2840_7462)">
                <path d="M12 11.8111C14.333 8.50312 12.167 3.98812 11 2.87012C11 6.26512 8.765 8.16912 7.333 9.57612C5.903 10.9841 5 13.1971 5 15.1641C5 18.8681 8.134 21.8701 12 21.8701C15.866 21.8701 19 18.8681 19 15.1641C19 13.4521 17.768 10.7611 16.667 9.57612C14.583 12.9291 13.41 12.9291 12 11.8111Z" stroke="black" stroke-linecap="round" stroke-linejoin="round"/>
              </g>
              <defs>
                <clipPath id="clip0_2840_7462">
                  <rect width="24" height="24" fill="white" transform="translate(0 0.870117)"/>
                </clipPath>
              </defs>
            </svg>
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="25" viewBox="0 0 24 25" fill="none">
              <g clip-path="url(#clip0_2840_7465)">
                <path d="M15.0004 3.87012C15.796 3.87012 16.5591 4.18619 17.1217 4.7488C17.6843 5.31141 18.0004 6.07447 18.0004 6.87012C18.4937 6.87001 18.9795 6.99157 19.4146 7.22403C19.8498 7.45649 20.2209 7.79268 20.495 8.20281C20.7692 8.61294 20.938 9.08436 20.9865 9.57531C21.035 10.0663 20.9616 10.5616 20.773 11.0174C20.5843 11.4733 20.2861 11.8755 19.9049 12.1886C19.5236 12.5017 19.071 12.7159 18.5872 12.8123C18.1033 12.9087 17.6032 12.8843 17.1311 12.7412C16.6589 12.5981 16.2293 12.3408 15.8804 11.9921L11.1224 16.7501C11.4658 17.0938 11.7206 17.5157 11.8648 17.9796C12.0091 18.4435 12.0386 18.9356 11.9507 19.4134C11.8628 19.8912 11.6601 20.3405 11.3602 20.7227C11.0603 21.1049 10.672 21.4085 10.2288 21.6075C9.78554 21.8064 9.30063 21.8948 8.81572 21.8649C8.33081 21.835 7.86041 21.6878 7.44497 21.436C7.02952 21.1841 6.68145 20.8351 6.43069 20.419C6.17993 20.0029 6.03398 19.5321 6.00539 19.0471V18.8701H5.82939C5.34422 18.8417 4.87323 18.6958 4.45692 18.445C4.04062 18.1943 3.69147 17.8461 3.43952 17.4305C3.18756 17.0149 3.04035 16.5444 3.01053 16.0593C2.98072 15.5742 3.0692 15.0891 3.26837 14.6458C3.46753 14.2025 3.77142 13.8142 4.15388 13.5144C4.53634 13.2145 4.98593 13.0121 5.46396 12.9244C5.942 12.8368 6.43416 12.8666 6.89812 13.0113C7.36207 13.156 7.78393 13.4113 8.12739 13.7551L12.8854 8.99712C12.4657 8.57767 12.1798 8.04319 12.0638 7.46125C11.9479 6.87932 12.0072 6.27607 12.2341 5.72781C12.461 5.17954 12.8454 4.71089 13.3387 4.38111C13.832 4.05133 14.412 3.87524 15.0054 3.87512L15.0004 3.87012Z" stroke="black" stroke-linecap="round" stroke-linejoin="round"/>
              </g>
              <defs>
                <clipPath id="clip0_2840_7465">
                  <rect width="24" height="24" fill="white" transform="translate(0 0.870117)"/>
                </clipPath>
              </defs>
            </svg>
          </h2>
          
          {features.length > 0 ? (
            <div className="flex overflow-x-scroll scrollbar-hide gap-[8px]">
              {features.map((feature, index) => (
                <div key={index} className="text-start">
                  <div className="w-[130px] mb-[8px] ">
                    <ResponsiveImage
                      src={feature.image}
                      variant="highlight"
                      alt={feature.title}
                      className="h-[154px] w-[130px] object-cover object-center"
                      loading="lazy"
                    />
                  </div>
                  <p className="text-[14px] text-black font-regular">{feature.title}</p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-8">No features available</p>
          )}
        </div>
      </div>

      <FoundersReviewCarousel reviews={property.founderReviews} />

      {amenityLines.length > 0 && (
        <div className=" px-[16px] py-[20px] md:px-[60px] lg:px-[120px]">
          <div className=" bg-white rounded-[16px] p-[20px]">
            <button
              type="button"
              className={`flex w-full items-center justify-between gap-3 text-left md:hidden ${mobileAmenitiesOpen ? 'mb-[20px]' : 'mb-0'}`}
              onClick={() => setMobileAmenitiesOpen((o) => !o)}
              aria-expanded={mobileAmenitiesOpen}
              aria-controls="property-detail-amenities"
            >
              <h2 className="m-0 flex min-w-0 flex-1 items-center gap-[3px] font-medium text-[18px] text-black">
                Amenities
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="25" viewBox="0 0 24 25" fill="none" aria-hidden>
            <g clip-path="url(#clip0_2846_7591)">
              <path d="M3 7.87012L9 4.87012L15 7.87012L21 4.87012V17.8701L15 20.8701L9 17.8701L3 20.8701V7.87012Z" stroke="black" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M9 12.8701V12.8801" stroke="black" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M6 13.8701V13.8801" stroke="black" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M17 15.8701L13 11.8701" stroke="black" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M13 15.8701L17 11.8701" stroke="black" stroke-linecap="round" stroke-linejoin="round"/>
            </g>
            <defs>
              <clipPath id="clip0_2846_7591">
                <rect width="24" height="24" fill="white" transform="translate(0 0.870117)"/>
              </clipPath>
            </defs>
          </svg>
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="25" viewBox="0 0 24 25" fill="none" aria-hidden>
                      <g clip-path="url(#clip0_2846_7626)">
                        <path d="M15.0004 3.87012C15.796 3.87012 16.5591 4.18619 17.1217 4.7488C17.6843 5.31141 18.0004 6.07447 18.0004 6.87012C18.4937 6.87001 18.9795 6.99157 19.4146 7.22403C19.8498 7.45649 20.2209 7.79268 20.495 8.20281C20.7692 8.61294 20.938 9.08436 20.9865 9.57531C21.035 10.0663 20.9616 10.5616 20.773 11.0174C20.5843 11.4733 20.2861 11.8755 19.9049 12.1886C19.5236 12.5017 19.071 12.7159 18.5872 12.8123C18.1033 12.9087 17.6032 12.8843 17.1311 12.7412C16.6589 12.5981 16.2293 12.3408 15.8804 11.9921L11.1224 16.7501C11.4658 17.0938 11.7206 17.5157 11.8648 17.9796C12.0091 18.4435 12.0386 18.9356 11.9507 19.4134C11.8628 19.8912 11.6601 20.3405 11.3602 20.7227C11.0603 21.1049 10.672 21.4085 10.2288 21.6075C9.78554 21.8064 9.30063 21.8948 8.81572 21.8649C8.33081 21.835 7.86041 21.6878 7.44497 21.436C7.02952 21.1841 6.68145 20.8351 6.43069 20.419C6.17993 20.0029 6.03398 19.5321 6.00539 19.0471V18.8701H5.82939C5.34422 18.8417 4.87323 18.6958 4.45692 18.445C4.04062 18.1943 3.69147 17.8461 3.43952 17.4305C3.18756 17.0149 3.04035 16.5444 3.01053 16.0593C2.98072 15.5742 3.0692 15.0891 3.26837 14.6458C3.46753 14.2025 3.77142 13.8142 4.15388 13.5144C4.53634 13.2145 4.98593 13.0121 5.46396 12.9244C5.942 12.8368 6.43416 12.8666 6.89812 13.0113C7.36207 13.156 7.78393 13.4113 8.12739 13.7551L12.8854 8.99712C12.4657 8.57767 12.1798 8.04319 12.0638 7.46125C11.9479 6.87932 12.0072 6.27607 12.2341 5.72781C12.461 5.17954 12.8454 4.71089 13.3387 4.38111C13.832 4.05133 14.412 3.87524 15.0054 3.87512L15.0004 3.87012Z" stroke="black" stroke-linecap="round" stroke-linejoin="round"/>
                      </g>
                      <defs>
                        <clipPath id="clip0_2846_7626">
                          <rect width="24" height="24" fill="white" transform="translate(0 0.870117)"/>
                        </clipPath>
                      </defs>
          </svg>
              </h2>
              <ChevronDown
                className={`h-5 w-5 shrink-0 text-black transition-transform duration-200 ${mobileAmenitiesOpen ? 'rotate-180' : ''}`}
                strokeWidth={2}
                aria-hidden
              />
            </button>
            <h2 className="mb-[20px] hidden font-medium text-[18px] text-black md:flex md:items-center md:gap-[3px]">
              Amenities
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="25" viewBox="0 0 24 25" fill="none" aria-hidden>
            <g clip-path="url(#clip_pd_am_desk_7591)">
              <path d="M3 7.87012L9 4.87012L15 7.87012L21 4.87012V17.8701L15 20.8701L9 17.8701L3 20.8701V7.87012Z" stroke="black" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M9 12.8701V12.8801" stroke="black" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M6 13.8701V13.8801" stroke="black" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M17 15.8701L13 11.8701" stroke="black" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M13 15.8701L17 11.8701" stroke="black" stroke-linecap="round" stroke-linejoin="round"/>
            </g>
            <defs>
              <clipPath id="clip_pd_am_desk_7591">
                <rect width="24" height="24" fill="white" transform="translate(0 0.870117)"/>
              </clipPath>
            </defs>
          </svg>
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="25" viewBox="0 0 24 25" fill="none" aria-hidden>
                      <g clip-path="url(#clip_pd_am_desk_7626)">
                        <path d="M15.0004 3.87012C15.796 3.87012 16.5591 4.18619 17.1217 4.7488C17.6843 5.31141 18.0004 6.07447 18.0004 6.87012C18.4937 6.87001 18.9795 6.99157 19.4146 7.22403C19.8498 7.45649 20.2209 7.79268 20.495 8.20281C20.7692 8.61294 20.938 9.08436 20.9865 9.57531C21.035 10.0663 20.9616 10.5616 20.773 11.0174C20.5843 11.4733 20.2861 11.8755 19.9049 12.1886C19.5236 12.5017 19.071 12.7159 18.5872 12.8123C18.1033 12.9087 17.6032 12.8843 17.1311 12.7412C16.6589 12.5981 16.2293 12.3408 15.8804 11.9921L11.1224 16.7501C11.4658 17.0938 11.7206 17.5157 11.8648 17.9796C12.0091 18.4435 12.0386 18.9356 11.9507 19.4134C11.8628 19.8912 11.6601 20.3405 11.3602 20.7227C11.0603 21.1049 10.672 21.4085 10.2288 21.6075C9.78554 21.8064 9.30063 21.8948 8.81572 21.8649C8.33081 21.835 7.86041 21.6878 7.44497 21.436C7.02952 21.1841 6.68145 20.8351 6.43069 20.419C6.17993 20.0029 6.03398 19.5321 6.00539 19.0471V18.8701H5.82939C5.34422 18.8417 4.87323 18.6958 4.45692 18.445C4.04062 18.1943 3.69147 17.8461 3.43952 17.4305C3.18756 17.0149 3.04035 16.5444 3.01053 16.0593C2.98072 15.5742 3.0692 15.0891 3.26837 14.6458C3.46753 14.2025 3.77142 13.8142 4.15388 13.5144C4.53634 13.2145 4.98593 13.0121 5.46396 12.9244C5.942 12.8368 6.43416 12.8666 6.89812 13.0113C7.36207 13.156 7.78393 13.4113 8.12739 13.7551L12.8854 8.99712C12.4657 8.57767 12.1798 8.04319 12.0638 7.46125C11.9479 6.87932 12.0072 6.27607 12.2341 5.72781C12.461 5.17954 12.8454 4.71089 13.3387 4.38111C13.832 4.05133 14.412 3.87524 15.0054 3.87512L15.0004 3.87012Z" stroke="black" stroke-linecap="round" stroke-linejoin="round"/>
                      </g>
                      <defs>
                        <clipPath id="clip_pd_am_desk_7626">
                          <rect width="24" height="24" fill="white" transform="translate(0 0.870117)"/>
                        </clipPath>
                      </defs>
          </svg>
            </h2>
            <div
              id="property-detail-amenities"
              className={`${mobileAmenitiesOpen ? '' : 'hidden '}md:block`}
            >
            <div className="flex items-center justify-center">
              <svg width="100%" height="1px">
                <line
                  x1="0"
                  y1="1"
                  x2="100%"
                  y2="1"
                  stroke="#f0f0f0"
                  strokeWidth="2"
                  strokeDasharray="10 4"
                />
              </svg>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 md:gap-x-[40px]">
              {amenityLines.map((line, index) => (
                <div key={index}>
                  <div className="flex items-center justify-between py-[20px]">
                    <span className="text-black text-[18px] leading-tight pr-4">{line}</span>
                  </div>
                  {index < amenityLines.length - 1 && (
                    <div className="flex md:hidden items-center justify-center">
                      <svg width="100%" height="1px">
                        <line
                          x1="0"
                          y1="1"
                          x2="100%"
                          y2="1"
                          stroke="#f0f0f0"
                          strokeWidth="2"
                          strokeDasharray="10 4"
                        />
                      </svg>
                    </div>
                  )}
                </div>
              ))}
            </div>
            </div>
          </div>
        </div>
      )}

      {/* Paw ratings (only when saved category scores exist) + house rules */}
      {showRatingsOrHouseRulesSection ? (
        <div className="pb-[20px] px-[16px] md:px-[60px] lg:px-[120px]">
          <div className="w-full">
            <div
              className={`grid gap-8 ${
                showPawRatingsCard && Object.keys(houseRules).length > 0
                  ? 'grid-cols-1 lg:grid-cols-2'
                  : 'grid-cols-1'
              }`}
            >
              {showPawRatingsCard ? (
              <div className="p-[20px] bg-white rounded-[16px]">
                {pawPct != null ? (
                <div className="mb-[40px]">
                  <h3 className="font-medium text-[18px] text-black flex items-center gap-[3px] mb-[10px]">
                    Paw Meter
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="25" viewBox="0 0 24 25" fill="none">
                      <g clip-path="url(#clip0_2840_7511)">
                        <path d="M12 15.8701C12 16.6658 12.3161 17.4288 12.8787 17.9914C13.4413 18.554 14.2044 18.8701 15 18.8701C15.7956 18.8701 16.5587 18.554 17.1213 17.9914C17.6839 17.4288 18 16.6658 18 15.8701C18 15.0745 17.6839 14.3114 17.1213 13.7488C16.5587 13.1862 15.7956 12.8701 15 12.8701C14.2044 12.8701 13.4413 13.1862 12.8787 13.7488C12.3161 14.3114 12 15.0745 12 15.8701Z" stroke="black" stroke-linecap="round" stroke-linejoin="round"/>
                        <path d="M13 18.3701V22.8701L15 21.3701L17 22.8701V18.3701" stroke="black" stroke-linecap="round" stroke-linejoin="round"/>
                        <path d="M10 19.8701H5C4.46957 19.8701 3.96086 19.6594 3.58579 19.2843C3.21071 18.9093 3 18.4006 3 17.8701V7.87012C3 6.77012 3.9 5.87012 5 5.87012H19C19.5304 5.87012 20.0391 6.08083 20.4142 6.4559C20.7893 6.83098 21 7.33968 21 7.87012V17.8701C20.9996 18.2208 20.9071 18.5653 20.7315 18.869C20.556 19.1726 20.3037 19.4248 20 19.6001" stroke="black" stroke-linecap="round" stroke-linejoin="round"/>
                        <path d="M6 9.87012H18" stroke="black" stroke-linecap="round" stroke-linejoin="round"/>
                        <path d="M6 12.8701H9" stroke="black" stroke-linecap="round" stroke-linejoin="round"/>
                        <path d="M6 15.8701H8" stroke="black" stroke-linecap="round" stroke-linejoin="round"/>
                      </g>
                      <defs>
                        <clipPath id="clip0_2840_7511">
                          <rect width="24" height="24" fill="white" transform="translate(0 0.870117)"/>
                        </clipPath>
                      </defs>
                    </svg>
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="25" viewBox="0 0 24 25" fill="none">
                      <g clip-path="url(#clip0_2840_7519)">
                        <path d="M15.0004 3.87012C15.796 3.87012 16.5591 4.18619 17.1217 4.7488C17.6843 5.31141 18.0004 6.07447 18.0004 6.87012C18.4937 6.87001 18.9795 6.99157 19.4146 7.22403C19.8498 7.45649 20.2209 7.79268 20.495 8.20281C20.7692 8.61294 20.938 9.08436 20.9865 9.57531C21.035 10.0663 20.9616 10.5616 20.773 11.0174C20.5843 11.4733 20.2861 11.8755 19.9049 12.1886C19.5236 12.5017 19.071 12.7159 18.5872 12.8123C18.1033 12.9087 17.6032 12.8843 17.1311 12.7412C16.6589 12.5981 16.2293 12.3408 15.8804 11.9921L11.1224 16.7501C11.4658 17.0938 11.7206 17.5157 11.8648 17.9796C12.0091 18.4435 12.0386 18.9356 11.9507 19.4134C11.8628 19.8912 11.6601 20.3405 11.3602 20.7227C11.0603 21.1049 10.672 21.4085 10.2288 21.6075C9.78554 21.8064 9.30063 21.8948 8.81572 21.8649C8.33081 21.835 7.86041 21.6878 7.44497 21.436C7.02952 21.1841 6.68145 20.8351 6.43069 20.419C6.17993 20.0029 6.03398 19.5321 6.00539 19.0471V18.8701H5.82939C5.34422 18.8417 4.87323 18.6958 4.45692 18.445C4.04062 18.1943 3.69147 17.8461 3.43952 17.4305C3.18756 17.0149 3.04035 16.5444 3.01053 16.0593C2.98072 15.5742 3.0692 15.0891 3.26837 14.6458C3.46753 14.2025 3.77142 13.8142 4.15388 13.5144C4.53634 13.2145 4.98593 13.0121 5.46396 12.9244C5.942 12.8368 6.43416 12.8666 6.89812 13.0113C7.36207 13.156 7.78393 13.4113 8.12739 13.7551L12.8854 8.99712C12.4657 8.57767 12.1798 8.04319 12.0638 7.46125C11.9479 6.87932 12.0072 6.27607 12.2341 5.72781C12.461 5.17954 12.8454 4.71089 13.3387 4.38111C13.832 4.05133 14.412 3.87524 15.0054 3.87512L15.0004 3.87012Z" stroke="black" stroke-linecap="round" stroke-linejoin="round"/>
                      </g>
                      <defs>
                        <clipPath id="clip0_2840_7519">
                          <rect width="24" height="24" fill="white" transform="translate(0 0.870117)"/>
                        </clipPath>
                      </defs>
                    </svg>
                  </h3>
                  <p className="text-[14px] text-[#8c8c8c] mb-[10px]">{pawPct}% Paws Loved the location</p>
                  <div className="bg-[#d9d9d9] rounded-full h-[9px] relative">
                    <div
                      className="bg-[#131313] rounded-full h-[9px] absolute top-0 left-0"
                      style={{ width: `${pawPct}%` }}
                    ></div>
                    <div
                      className="absolute "
                      style={{ left: `${pawPct}%`, transform: 'translateX(-50%)' }}
                    >
                      <svg className="translate-y-[-50%]" xmlns="http://www.w3.org/2000/svg" width="33" height="34" viewBox="0 0 33 34" fill="none">
                        <path d="M12.3846 14.9C14.0836 14.2499 15.6237 13.8533 17.0413 14.7391C18.4587 15.625 18.7772 17.183 18.9378 18.9949C19.1011 20.8381 19.1051 23.0005 19.7695 25.3406C20.3462 27.3719 19.5186 28.9826 18.1698 29.8414C16.8358 30.6906 14.9895 30.8056 13.5169 29.8858C12.4276 29.2049 11.8174 28.4903 11.3157 27.8515C10.8104 27.2083 10.4437 26.6868 9.81937 26.2965C9.19509 25.9063 8.56511 25.8053 7.76527 25.6332C6.97133 25.4623 6.06182 25.2275 4.97242 24.5471L4.97149 24.5468C3.49963 23.6266 2.79419 21.917 2.97258 20.3457C3.15306 18.7568 4.23831 17.307 6.31693 16.9348C8.71136 16.5063 10.6564 15.5612 12.3846 14.9Z" fill="#FF5E4C" stroke="#FF5E4C" stroke-width="0.544625"/>
                        <path d="M24.5649 16.8299C25.6877 16.4163 26.8643 16.3639 27.6474 17.0262C28.4244 17.6834 28.6087 18.8065 28.4022 19.9725C28.1937 21.1498 27.5789 22.4423 26.6529 23.5372C25.2064 25.2476 22.8305 25.7445 21.3402 24.4848C19.85 23.2245 19.9445 20.798 21.3912 19.0872C22.2535 18.0678 23.4264 17.2493 24.5649 16.8299Z" fill="#FF5E4C" stroke="#FF5E4C" stroke-width="0.544625"/>
                        <path d="M15.8053 3.96642C16.8017 3.3262 17.9089 3.06304 18.8149 3.5266C19.728 3.99406 20.1338 5.1001 20.1849 6.29574C20.2367 7.50795 19.9331 8.90562 19.3248 10.0942C18.3038 12.0881 16.1018 13.1108 14.365 12.2221C12.6279 11.3328 12.1701 8.94848 13.1904 6.95441L13.2975 6.74442L13.3012 6.74569C13.9493 5.56197 14.8547 4.57718 15.8053 3.96642Z" fill="#FF5E4C" stroke="#FF5E4C" stroke-width="0.544625"/>
                        <path d="M23.2529 8.21173C24.35 7.73351 25.522 7.61276 26.3423 8.22874C27.1561 8.83977 27.4051 9.94992 27.2667 11.1259C27.1269 12.3133 26.5881 13.6395 25.7272 14.7863C24.382 16.5781 22.0388 17.2115 20.4779 16.0395C18.9174 14.8682 18.8716 12.4411 20.2166 10.6494C21.0182 9.58159 22.1408 8.69659 23.2529 8.21173Z" fill="#FF5E4C" stroke="#FF5E4C" stroke-width="0.544625"/>
                        <path d="M10.3345 6.20901C11.2732 6.62261 11.7419 7.70331 11.8624 8.89398C11.9845 10.1012 11.7624 11.5136 11.2239 12.7355C10.3236 14.7789 8.19655 15.9264 6.41418 15.1544L6.40679 15.1518L6.39603 15.1471C4.61025 14.3602 4.01459 12.0071 4.91807 9.95704C5.49627 8.64484 6.38837 7.52595 7.35514 6.82234C8.31281 6.12537 9.40304 5.79847 10.3345 6.20901Z" fill="#FF5E4C" stroke="#FF5E4C" stroke-width="0.544625"/>
                      </svg>
                    </div>
                  </div>
                </div>
                ) : null}

                <div className="space-y-[20px]">
                  {ratingRows.map(([category, data]) => (
                    <div key={category} className="flex items-start space-x-[10px]">
                      <div className="flex items-center space-x-[-3px]">
                      <div className="bg-[#f0f0f0]  rounded-full w-[34px] h-[34px] flex items-center justify-center">
                      {getCategoryIcon(category)}
                      </div>
                      <div className="bg-black text-white rounded-full w-[34px] h-[34px] flex items-center justify-center text-[14px] font-normal">
                        {data.score}
                      </div>
                      </div>
                      <div className="flex-1">
                          <h4 className="font-normal text-black capitalize">{category}</h4>
                     
                        <p className="text-[14px] font-light leading-[1.1] text-[#8c8c8c]">{data.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              ) : null}

              {Object.keys(houseRules).length > 0 ? (
              <div className='p-[20px] bg-white rounded-[16px]'>
                <button
                  type="button"
                  className={`flex w-full items-center justify-between gap-3 text-left md:hidden ${mobileHouseRulesOpen ? 'mb-[20px]' : 'mb-0'}`}
                  onClick={() => setMobileHouseRulesOpen((o) => !o)}
                  aria-expanded={mobileHouseRulesOpen}
                  aria-controls="property-detail-house-rules"
                >
                  <h2 className="m-0 flex min-w-0 flex-1 items-center gap-[3px] font-medium text-[18px] text-black">
                    House Rules
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="25" viewBox="0 0 24 25" fill="none" aria-hidden>
                    <g clip-path="url(#clip_pd_hr_mb_7621)">
                      <path d="M5 13.8701C5 13.3397 5.21071 12.831 5.58579 12.4559C5.96086 12.0808 6.46957 11.8701 7 11.8701H17C17.5304 11.8701 18.0391 12.0808 18.4142 12.4559C18.7893 12.831 19 13.3397 19 13.8701V19.8701C19 20.4006 18.7893 20.9093 18.4142 21.2843C18.0391 21.6594 17.5304 21.8701 17 21.8701H7C6.46957 21.8701 5.96086 21.6594 5.58579 21.2843C5.21071 20.9093 5 20.4006 5 19.8701V13.8701Z" stroke="black" stroke-linecap="round" stroke-linejoin="round"/>
                      <path d="M11 16.8701C11 17.1353 11.1054 17.3897 11.2929 17.5772C11.4804 17.7648 11.7348 17.8701 12 17.8701C12.2652 17.8701 12.5196 17.7648 12.7071 17.5772C12.8946 17.3897 13 17.1353 13 16.8701C13 16.6049 12.8946 16.3505 12.7071 16.163C12.5196 15.9755 12.2652 15.8701 12 15.8701C11.7348 15.8701 11.4804 15.9755 11.2929 16.163C11.1054 16.3505 11 16.6049 11 16.8701Z" stroke="black" stroke-linecap="round" stroke-linejoin="round"/>
                      <path d="M8 11.8701V7.87012C8 6.80925 8.42143 5.79184 9.17157 5.04169C9.92172 4.29154 10.9391 3.87012 12 3.87012C13.0609 3.87012 14.0783 4.29154 14.8284 5.04169C15.5786 5.79184 16 6.80925 16 7.87012V11.8701" stroke="black" stroke-linecap="round" stroke-linejoin="round"/>
                    </g>
                    <defs>
                      <clipPath id="clip_pd_hr_mb_7621">
                        <rect width="24" height="24" fill="white" transform="translate(0 0.870117)"/>
                      </clipPath>
                    </defs>
                  </svg>
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="25" viewBox="0 0 24 25" fill="none" aria-hidden>
                    <g clip-path="url(#clip_pd_hr_mb_7626)">
                      <path d="M15.0004 3.87012C15.796 3.87012 16.5591 4.18619 17.1217 4.7488C17.6843 5.31141 18.0004 6.07447 18.0004 6.87012C18.4937 6.87001 18.9795 6.99157 19.4146 7.22403C19.8498 7.45649 20.2209 7.79268 20.495 8.20281C20.7692 8.61294 20.938 9.08436 20.9865 9.57531C21.035 10.0663 20.9616 10.5616 20.773 11.0174C20.5843 11.4733 20.2861 11.8755 19.9049 12.1886C19.5236 12.5017 19.071 12.7159 18.5872 12.8123C18.1033 12.9087 17.6032 12.8843 17.1311 12.7412C16.6589 12.5981 16.2293 12.3408 15.8804 11.9921L11.1224 16.7501C11.4658 17.0938 11.7206 17.5157 11.8648 17.9796C12.0091 18.4435 12.0386 18.9356 11.9507 19.4134C11.8628 19.8912 11.6601 20.3405 11.3602 20.7227C11.0603 21.1049 10.672 21.4085 10.2288 21.6075C9.78554 21.8064 9.30063 21.8948 8.81572 21.8649C8.33081 21.835 7.86041 21.6878 7.44497 21.436C7.02952 21.1841 6.68145 20.8351 6.43069 20.419C6.17993 20.0029 6.03398 19.5321 6.00539 19.0471V18.8701H5.82939C5.34422 18.8417 4.87323 18.6958 4.45692 18.445C4.04062 18.1943 3.69147 17.8461 3.43952 17.4305C3.18756 17.0149 3.04035 16.5444 3.01053 16.0593C2.98072 15.5742 3.0692 15.0891 3.26837 14.6458C3.46753 14.2025 3.77142 13.8142 4.15388 13.5144C4.53634 13.2145 4.98593 13.0121 5.46396 12.9244C5.942 12.8368 6.43416 12.8666 6.89812 13.0113C7.36207 13.156 7.78393 13.4113 8.12739 13.7551L12.8854 8.99712C12.4657 8.57767 12.1798 8.04319 12.0638 7.46125C11.9479 6.87932 12.0072 6.27607 12.2341 5.72781C12.461 5.17954 12.8454 4.71089 13.3387 4.38111C13.832 4.05133 14.412 3.87524 15.0054 3.87512L15.0004 3.87012Z" stroke="black" stroke-linecap="round" stroke-linejoin="round"/>
                    </g>
                    <defs>
                      <clipPath id="clip_pd_hr_mb_7626">
                        <rect width="24" height="24" fill="white" transform="translate(0 0.870117)"/>
                      </clipPath>
                    </defs>
                  </svg>
                  </h2>
                  <ChevronDown
                    className={`h-5 w-5 shrink-0 text-black transition-transform duration-200 ${mobileHouseRulesOpen ? 'rotate-180' : ''}`}
                    strokeWidth={2}
                    aria-hidden
                  />
                </button>
                <h2 className="mb-[20px] hidden font-medium text-[18px] text-black md:flex md:items-center md:gap-[3px]">
                  House Rules
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="25" viewBox="0 0 24 25" fill="none" aria-hidden>
                    <g clip-path="url(#clip_pd_hr_dk_7621)">
                      <path d="M5 13.8701C5 13.3397 5.21071 12.831 5.58579 12.4559C5.96086 12.0808 6.46957 11.8701 7 11.8701H17C17.5304 11.8701 18.0391 12.0808 18.4142 12.4559C18.7893 12.831 19 13.3397 19 13.8701V19.8701C19 20.4006 18.7893 20.9093 18.4142 21.2843C18.0391 21.6594 17.5304 21.8701 17 21.8701H7C6.46957 21.8701 5.96086 21.6594 5.58579 21.2843C5.21071 20.9093 5 20.4006 5 19.8701V13.8701Z" stroke="black" stroke-linecap="round" stroke-linejoin="round"/>
                      <path d="M11 16.8701C11 17.1353 11.1054 17.3897 11.2929 17.5772C11.4804 17.7648 11.7348 17.8701 12 17.8701C12.2652 17.8701 12.5196 17.7648 12.7071 17.5772C12.8946 17.3897 13 17.1353 13 16.8701C13 16.6049 12.8946 16.3505 12.7071 16.163C12.5196 15.9755 12.2652 15.8701 12 15.8701C11.7348 15.8701 11.4804 15.9755 11.2929 16.163C11.1054 16.3505 11 16.6049 11 16.8701Z" stroke="black" stroke-linecap="round" stroke-linejoin="round"/>
                      <path d="M8 11.8701V7.87012C8 6.80925 8.42143 5.79184 9.17157 5.04169C9.92172 4.29154 10.9391 3.87012 12 3.87012C13.0609 3.87012 14.0783 4.29154 14.8284 5.04169C15.5786 5.79184 16 6.80925 16 7.87012V11.8701" stroke="black" stroke-linecap="round" stroke-linejoin="round"/>
                    </g>
                    <defs>
                      <clipPath id="clip_pd_hr_dk_7621">
                        <rect width="24" height="24" fill="white" transform="translate(0 0.870117)"/>
                      </clipPath>
                    </defs>
                  </svg>
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="25" viewBox="0 0 24 25" fill="none" aria-hidden>
                    <g clip-path="url(#clip_pd_hr_dk_7626)">
                      <path d="M15.0004 3.87012C15.796 3.87012 16.5591 4.18619 17.1217 4.7488C17.6843 5.31141 18.0004 6.07447 18.0004 6.87012C18.4937 6.87001 18.9795 6.99157 19.4146 7.22403C19.8498 7.45649 20.2209 7.79268 20.495 8.20281C20.7692 8.61294 20.938 9.08436 20.9865 9.57531C21.035 10.0663 20.9616 10.5616 20.773 11.0174C20.5843 11.4733 20.2861 11.8755 19.9049 12.1886C19.5236 12.5017 19.071 12.7159 18.5872 12.8123C18.1033 12.9087 17.6032 12.8843 17.1311 12.7412C16.6589 12.5981 16.2293 12.3408 15.8804 11.9921L11.1224 16.7501C11.4658 17.0938 11.7206 17.5157 11.8648 17.9796C12.0091 18.4435 12.0386 18.9356 11.9507 19.4134C11.8628 19.8912 11.6601 20.3405 11.3602 20.7227C11.0603 21.1049 10.672 21.4085 10.2288 21.6075C9.78554 21.8064 9.30063 21.8948 8.81572 21.8649C8.33081 21.835 7.86041 21.6878 7.44497 21.436C7.02952 21.1841 6.68145 20.8351 6.43069 20.419C6.17993 20.0029 6.03398 19.5321 6.00539 19.0471V18.8701H5.82939C5.34422 18.8417 4.87323 18.6958 4.45692 18.445C4.04062 18.1943 3.69147 17.8461 3.43952 17.4305C3.18756 17.0149 3.04035 16.5444 3.01053 16.0593C2.98072 15.5742 3.0692 15.0891 3.26837 14.6458C3.46753 14.2025 3.77142 13.8142 4.15388 13.5144C4.53634 13.2145 4.98593 13.0121 5.46396 12.9244C5.942 12.8368 6.43416 12.8666 6.89812 13.0113C7.36207 13.156 7.78393 13.4113 8.12739 13.7551L12.8854 8.99712C12.4657 8.57767 12.1798 8.04319 12.0638 7.46125C11.9479 6.87932 12.0072 6.27607 12.2341 5.72781C12.461 5.17954 12.8454 4.71089 13.3387 4.38111C13.832 4.05133 14.412 3.87524 15.0054 3.87512L15.0004 3.87012Z" stroke="black" stroke-linecap="round" stroke-linejoin="round"/>
                    </g>
                    <defs>
                      <clipPath id="clip_pd_hr_dk_7626">
                        <rect width="24" height="24" fill="white" transform="translate(0 0.870117)"/>
                      </clipPath>
                    </defs>
                  </svg>
                </h2>
                <div
                  id="property-detail-house-rules"
                  className={`${mobileHouseRulesOpen ? '' : 'hidden '}md:block`}
                >
                <FilterDashedSeparator className="mb-[20px]" />
                <div className="space-y-[20px]">
                  {Object.keys(houseRules).length > 0 ? (
                    Object.entries(houseRules).map(([rule, value]) => (
                      <div key={rule} className="flex flex-col items-start justify-between">
                        <span className="text-black text-[18px] leading-tight mb-[10px]">{rule}</span>
                        <span className="font-normal text-[14px] leading-tight text-[#8c8c8c]">{value}</span>
                      </div>
                    ))
                  ) : (
                    <div className="flex flex-column items-center justify-between">
                      <span className="text-black text-[18px]">Check in:</span>
                      <span className="font-normal text-[14px] text-[#8c8c8c]">2:00 PM</span>
                    </div>
                  )}
                </div>
                </div>
              </div>
              ) : null}

            </div>
          </div>
        </div>
      ) : null}
      
      {nearestLandmarks.length > 0 ? (
        <div className=" px-[16px] pb-[20px] md:px-[60px] lg:px-[120px]">
          <div className=" bg-white rounded-[16px] p-[20px] ">
            <button
              type="button"
              className={`flex w-full items-center justify-between gap-3 text-left md:hidden ${mobileAroundOpen ? 'mb-[20px]' : 'mb-0'}`}
              onClick={() => setMobileAroundOpen((o) => !o)}
              aria-expanded={mobileAroundOpen}
              aria-controls="property-detail-around"
            >
              <h2 className="m-0 flex min-w-0 flex-1 items-center gap-[3px] font-medium text-[18px] text-black">
                Around the Property
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="25" viewBox="0 0 24 25" fill="none" aria-hidden>
                <g clip-path="url(#clip_pd_ar_mb_7591)">
                  <path d="M3 7.87012L9 4.87012L15 7.87012L21 4.87012V17.8701L15 20.8701L9 17.8701L3 20.8701V7.87012Z" stroke="black" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M9 12.8701V12.8801" stroke="black" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M6 13.8701V13.8801" stroke="black" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M17 15.8701L13 11.8701" stroke="black" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M13 15.8701L17 11.8701" stroke="black" stroke-linecap="round" stroke-linejoin="round"/>
                </g>
                <defs>
                  <clipPath id="clip_pd_ar_mb_7591">
                    <rect width="24" height="24" fill="white" transform="translate(0 0.870117)"/>
                  </clipPath>
                </defs>
              </svg>
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="25" viewBox="0 0 24 25" fill="none" aria-hidden>
                <g clip-path="url(#clip_pd_ar_mb_7626)">
                  <path d="M15.0004 3.87012C15.796 3.87012 16.5591 4.18619 17.1217 4.7488C17.6843 5.31141 18.0004 6.07447 18.0004 6.87012C18.4937 6.87001 18.9795 6.99157 19.4146 7.22403C19.8498 7.45649 20.2209 7.79268 20.495 8.20281C20.7692 8.61294 20.938 9.08436 20.9865 9.57531C21.035 10.0663 20.9616 10.5616 20.773 11.0174C20.5843 11.4733 20.2861 11.8755 19.9049 12.1886C19.5236 12.5017 19.071 12.7159 18.5872 12.8123C18.1033 12.9087 17.6032 12.8843 17.1311 12.7412C16.6589 12.5981 16.2293 12.3408 15.8804 11.9921L11.1224 16.7501C11.4658 17.0938 11.7206 17.5157 11.8648 17.9796C12.0091 18.4435 12.0386 18.9356 11.9507 19.4134C11.8628 19.8912 11.6601 20.3405 11.3602 20.7227C11.0603 21.1049 10.672 21.4085 10.2288 21.6075C9.78554 21.8064 9.30063 21.8948 8.81572 21.8649C8.33081 21.835 7.86041 21.6878 7.44497 21.436C7.02952 21.1841 6.68145 20.8351 6.43069 20.419C6.17993 20.0029 6.03398 19.5321 6.00539 19.0471V18.8701H5.82939C5.34422 18.8417 4.87323 18.6958 4.45692 18.445C4.04062 18.1943 3.69147 17.8461 3.43952 17.4305C3.18756 17.0149 3.04035 16.5444 3.01053 16.0593C2.98072 15.5742 3.0692 15.0891 3.26837 14.6458C3.46753 14.2025 3.77142 13.8142 4.15388 13.5144C4.53634 13.2145 4.98593 13.0121 5.46396 12.9244C5.942 12.8368 6.43416 12.8666 6.89812 13.0113C7.36207 13.156 7.78393 13.4113 8.12739 13.7551L12.8854 8.99712C12.4657 8.57767 12.1798 8.04319 12.0638 7.46125C11.9479 6.87932 12.0072 6.27607 12.2341 5.72781C12.461 5.17954 12.8454 4.71089 13.3387 4.38111C13.832 4.05133 14.412 3.87524 15.0054 3.87512L15.0004 3.87012Z" stroke="black" stroke-linecap="round" stroke-linejoin="round"/>
                </g>
                <defs>
                  <clipPath id="clip_pd_ar_mb_7626">
                    <rect width="24" height="24" fill="white" transform="translate(0 0.870117)"/>
                  </clipPath>
                </defs>
              </svg>
              </h2>
              <ChevronDown
                className={`h-5 w-5 shrink-0 text-black transition-transform duration-200 ${mobileAroundOpen ? 'rotate-180' : ''}`}
                strokeWidth={2}
                aria-hidden
              />
            </button>
            <h2 className="mb-[20px] hidden font-medium text-[18px] text-black md:flex md:items-center md:gap-[3px]">
              Around the Property
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="25" viewBox="0 0 24 25" fill="none" aria-hidden>
                <g clip-path="url(#clip_pd_ar_dk_7591)">
                  <path d="M3 7.87012L9 4.87012L15 7.87012L21 4.87012V17.8701L15 20.8701L9 17.8701L3 20.8701V7.87012Z" stroke="black" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M9 12.8701V12.8801" stroke="black" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M6 13.8701V13.8801" stroke="black" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M17 15.8701L13 11.8701" stroke="black" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M13 15.8701L17 11.8701" stroke="black" stroke-linecap="round" stroke-linejoin="round"/>
                </g>
                <defs>
                  <clipPath id="clip_pd_ar_dk_7591">
                    <rect width="24" height="24" fill="white" transform="translate(0 0.870117)"/>
                  </clipPath>
                </defs>
              </svg>
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="25" viewBox="0 0 24 25" fill="none" aria-hidden>
                <g clip-path="url(#clip_pd_ar_dk_7626)">
                  <path d="M15.0004 3.87012C15.796 3.87012 16.5591 4.18619 17.1217 4.7488C17.6843 5.31141 18.0004 6.07447 18.0004 6.87012C18.4937 6.87001 18.9795 6.99157 19.4146 7.22403C19.8498 7.45649 20.2209 7.79268 20.495 8.20281C20.7692 8.61294 20.938 9.08436 20.9865 9.57531C21.035 10.0663 20.9616 10.5616 20.773 11.0174C20.5843 11.4733 20.2861 11.8755 19.9049 12.1886C19.5236 12.5017 19.071 12.7159 18.5872 12.8123C18.1033 12.9087 17.6032 12.8843 17.1311 12.7412C16.6589 12.5981 16.2293 12.3408 15.8804 11.9921L11.1224 16.7501C11.4658 17.0938 11.7206 17.5157 11.8648 17.9796C12.0091 18.4435 12.0386 18.9356 11.9507 19.4134C11.8628 19.8912 11.6601 20.3405 11.3602 20.7227C11.0603 21.1049 10.672 21.4085 10.2288 21.6075C9.78554 21.8064 9.30063 21.8948 8.81572 21.8649C8.33081 21.835 7.86041 21.6878 7.44497 21.436C7.02952 21.1841 6.68145 20.8351 6.43069 20.419C6.17993 20.0029 6.03398 19.5321 6.00539 19.0471V18.8701H5.82939C5.34422 18.8417 4.87323 18.6958 4.45692 18.445C4.04062 18.1943 3.69147 17.8461 3.43952 17.4305C3.18756 17.0149 3.04035 16.5444 3.01053 16.0593C2.98072 15.5742 3.0692 15.0891 3.26837 14.6458C3.46753 14.2025 3.77142 13.8142 4.15388 13.5144C4.53634 13.2145 4.98593 13.0121 5.46396 12.9244C5.942 12.8368 6.43416 12.8666 6.89812 13.0113C7.36207 13.156 7.78393 13.4113 8.12739 13.7551L12.8854 8.99712C12.4657 8.57767 12.1798 8.04319 12.0638 7.46125C11.9479 6.87932 12.0072 6.27607 12.2341 5.72781C12.461 5.17954 12.8454 4.71089 13.3387 4.38111C13.832 4.05133 14.412 3.87524 15.0054 3.87512L15.0004 3.87012Z" stroke="black" stroke-linecap="round" stroke-linejoin="round"/>
                </g>
                <defs>
                  <clipPath id="clip_pd_ar_dk_7626">
                    <rect width="24" height="24" fill="white" transform="translate(0 0.870117)"/>
                  </clipPath>
                </defs>
              </svg>
            </h2>
            <div
              id="property-detail-around"
              className={`${mobileAroundOpen ? '' : 'hidden '}md:block`}
            >
            <div className="flex items-center justify-center">
              <svg width="100%" height="1px">
                <line
                  x1="0"
                  y1="1"
                  x2="100%"
                  y2="1"
                  stroke="#f0f0f0"
                  strokeWidth="2"
                  strokeDasharray="10 4"
                />
              </svg>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 md:gap-x-[40px] ">
              {nearestLandmarks.map((place, index) => (
                <div key={index}>
                  <div className="flex items-center justify-between py-[20px]">
                    <span className="text-black text-[18px]">{place.location || place.name}</span>
                    <span className="text-[#8c8c8c] text-[14px]">{place.distance}</span>
                  </div>
                  {index < nearestLandmarks.length - 1 && (
                    <div className="flex md:hidden items-center justify-center">
                      <svg width="100%" height="1px">
                        <line
                          x1="0"
                          y1="1"
                          x2="100%"
                          y2="1"
                          stroke="#f0f0f0"
                          strokeWidth="2"
                          strokeDasharray="10 4"
                        />
                      </svg>
                    </div>
                  )}
                </div>
              ))}
            </div>
            </div>
          </div>
        </div>
      ) : null}

      {aboutPropertyText ? (
        <div className="px-[16px] pb-[20px] md:px-[60px] lg:px-[120px]">
          <div className="bg-white rounded-[16px] p-[20px]">
            <button
              type="button"
              className={`flex w-full items-center justify-between gap-3 text-left md:hidden ${mobileAboutOpen ? 'mb-[20px]' : 'mb-0'}`}
              onClick={() => setMobileAboutOpen((o) => !o)}
              aria-expanded={mobileAboutOpen}
              aria-controls="property-detail-about"
            >
              <h2 className="m-0 flex min-w-0 flex-1 items-center gap-[6px] font-medium text-[18px] text-black">
  
                About the Property
                <Home className="h-5 w-5 shrink-0" strokeWidth={1} aria-hidden />
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="25" viewBox="0 0 24 25" fill="none" aria-hidden>
                <g clip-path="url(#clip_pd_ar_dk_7626)">
                  <path d="M15.0004 3.87012C15.796 3.87012 16.5591 4.18619 17.1217 4.7488C17.6843 5.31141 18.0004 6.07447 18.0004 6.87012C18.4937 6.87001 18.9795 6.99157 19.4146 7.22403C19.8498 7.45649 20.2209 7.79268 20.495 8.20281C20.7692 8.61294 20.938 9.08436 20.9865 9.57531C21.035 10.0663 20.9616 10.5616 20.773 11.0174C20.5843 11.4733 20.2861 11.8755 19.9049 12.1886C19.5236 12.5017 19.071 12.7159 18.5872 12.8123C18.1033 12.9087 17.6032 12.8843 17.1311 12.7412C16.6589 12.5981 16.2293 12.3408 15.8804 11.9921L11.1224 16.7501C11.4658 17.0938 11.7206 17.5157 11.8648 17.9796C12.0091 18.4435 12.0386 18.9356 11.9507 19.4134C11.8628 19.8912 11.6601 20.3405 11.3602 20.7227C11.0603 21.1049 10.672 21.4085 10.2288 21.6075C9.78554 21.8064 9.30063 21.8948 8.81572 21.8649C8.33081 21.835 7.86041 21.6878 7.44497 21.436C7.02952 21.1841 6.68145 20.8351 6.43069 20.419C6.17993 20.0029 6.03398 19.5321 6.00539 19.0471V18.8701H5.82939C5.34422 18.8417 4.87323 18.6958 4.45692 18.445C4.04062 18.1943 3.69147 17.8461 3.43952 17.4305C3.18756 17.0149 3.04035 16.5444 3.01053 16.0593C2.98072 15.5742 3.0692 15.0891 3.26837 14.6458C3.46753 14.2025 3.77142 13.8142 4.15388 13.5144C4.53634 13.2145 4.98593 13.0121 5.46396 12.9244C5.942 12.8368 6.43416 12.8666 6.89812 13.0113C7.36207 13.156 7.78393 13.4113 8.12739 13.7551L12.8854 8.99712C12.4657 8.57767 12.1798 8.04319 12.0638 7.46125C11.9479 6.87932 12.0072 6.27607 12.2341 5.72781C12.461 5.17954 12.8454 4.71089 13.3387 4.38111C13.832 4.05133 14.412 3.87524 15.0054 3.87512L15.0004 3.87012Z" stroke="black" stroke-linecap="round" stroke-linejoin="round"/>
                </g>
                <defs>
                  <clipPath id="clip_pd_ar_dk_7626">
                    <rect width="24" height="24" fill="white" transform="translate(0 0.870117)"/>
                  </clipPath>
                </defs>
              </svg>
              </h2>
              <ChevronDown
                className={`h-5 w-5 shrink-0 text-black transition-transform duration-200 ${mobileAboutOpen ? 'rotate-180' : ''}`}
                strokeWidth={2}
                aria-hidden
              />
            </button>
            <h2 className="mb-[20px] hidden font-medium text-[18px] text-black md:flex md:items-center md:gap-[6px]">
              
              About the Property
              <Home className="h-5 w-5 shrink-0" strokeWidth={1} aria-hidden />
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="25" viewBox="0 0 24 25" fill="none" aria-hidden>
                <g clip-path="url(#clip_pd_ar_dk_7626)">
                  <path d="M15.0004 3.87012C15.796 3.87012 16.5591 4.18619 17.1217 4.7488C17.6843 5.31141 18.0004 6.07447 18.0004 6.87012C18.4937 6.87001 18.9795 6.99157 19.4146 7.22403C19.8498 7.45649 20.2209 7.79268 20.495 8.20281C20.7692 8.61294 20.938 9.08436 20.9865 9.57531C21.035 10.0663 20.9616 10.5616 20.773 11.0174C20.5843 11.4733 20.2861 11.8755 19.9049 12.1886C19.5236 12.5017 19.071 12.7159 18.5872 12.8123C18.1033 12.9087 17.6032 12.8843 17.1311 12.7412C16.6589 12.5981 16.2293 12.3408 15.8804 11.9921L11.1224 16.7501C11.4658 17.0938 11.7206 17.5157 11.8648 17.9796C12.0091 18.4435 12.0386 18.9356 11.9507 19.4134C11.8628 19.8912 11.6601 20.3405 11.3602 20.7227C11.0603 21.1049 10.672 21.4085 10.2288 21.6075C9.78554 21.8064 9.30063 21.8948 8.81572 21.8649C8.33081 21.835 7.86041 21.6878 7.44497 21.436C7.02952 21.1841 6.68145 20.8351 6.43069 20.419C6.17993 20.0029 6.03398 19.5321 6.00539 19.0471V18.8701H5.82939C5.34422 18.8417 4.87323 18.6958 4.45692 18.445C4.04062 18.1943 3.69147 17.8461 3.43952 17.4305C3.18756 17.0149 3.04035 16.5444 3.01053 16.0593C2.98072 15.5742 3.0692 15.0891 3.26837 14.6458C3.46753 14.2025 3.77142 13.8142 4.15388 13.5144C4.53634 13.2145 4.98593 13.0121 5.46396 12.9244C5.942 12.8368 6.43416 12.8666 6.89812 13.0113C7.36207 13.156 7.78393 13.4113 8.12739 13.7551L12.8854 8.99712C12.4657 8.57767 12.1798 8.04319 12.0638 7.46125C11.9479 6.87932 12.0072 6.27607 12.2341 5.72781C12.461 5.17954 12.8454 4.71089 13.3387 4.38111C13.832 4.05133 14.412 3.87524 15.0054 3.87512L15.0004 3.87012Z" stroke="black" stroke-linecap="round" stroke-linejoin="round"/>
                </g>
                <defs>
                  <clipPath id="clip_pd_ar_dk_7626">
                    <rect width="24" height="24" fill="white" transform="translate(0 0.870117)"/>
                  </clipPath>
                </defs>
              </svg>
            </h2>
            <div
              id="property-detail-about"
              className={`${mobileAboutOpen ? '' : 'hidden '}md:block`}
            >
              <FilterDashedSeparator />
              <p className="pt-[20px] text-[16px] font-normal leading-[1.5] text-[#3a3a38] whitespace-pre-line">
                {aboutPropertyText}
              </p>
            </div>
          </div>
        </div>
      ) : null}

      <PawStories/>
      <MeetTheFounders/>
    </div>
  );
};

export default PropertyDetail; 